import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setCompanies, NO_COMPANY } from '../../store';
import { InputText } from 'primereact/inputtext';
import DateInput from '../common/DateInput';
import FormDialog from '../common/FormDialog';
import { companiesApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';

const EMPTY = {
    id: 0, tin: '', name: '', address: '', country: '', email: '', phone: '', fax: '', city: '',
    mainPerson: '', mainPersonPosition: '', contractIssueDate: null, contractExpiryDate: null,
};

/* Forma kompanije (nova / izmena). `onSaved(company)` se zove posle uspešnog snimanja. */
export default function CompanyForm({ id = 0, onClose, onSaved }) {
    const alerts = useAlerts();
    const dispatch = useDispatch();
    const [f, setF] = useState(EMPTY);
    const [busy, setBusy] = useState(!!id);
    const [dirty, setDirty] = useState(false);
    const [title, setTitle] = useState(id ? '' : 'Nova kompanija');
    const touched = dirty; // neispravna polja se označavaju posle prve izmene

    useEffect(() => {
        if (!id) return;
        companiesApi.get(id).then((c) => { setF({ ...EMPTY, ...c }); setTitle(`Kompanija:  ${c.name} [${c.tin}]`); }).catch(onClose).finally(() => setBusy(false));
    }, [id]);

    const set = (k) => (e) => { setF({ ...f, [k]: e.target ? e.target.value : e.value }); setDirty(true); };
    const req = ['tin', 'name', 'address', 'country'];
    const invalid = (k) => touched && (req.includes(k) ? !String(f[k] || '').trim() : false);
    const dateInvalid = touched && !f.contractIssueDate;
    const valid = req.every((k) => String(f[k] || '').trim()) && !!f.contractIssueDate;

    const save = async () => {
        if (!valid) return;
        try {
            const saved = f.id ? await companiesApi.update(f) : await companiesApi.create(f);
            alerts.success(f.id ? 'Ažuriranje kompanije uspešno' : 'Dodavanje kompanije uspešno');
            const list = await companiesApi.all();
            dispatch(setCompanies([NO_COMPANY, ...list]));
            if (onSaved) await onSaved(saved);
            onClose();
        } catch { /* toast */ }
    };

    const text = (k, label) => (
        <div className="field">
            <label htmlFor={`co-${k}`}>{label}{req.includes(k) && <span className="req" aria-hidden="true">*</span>}</label>
            <InputText id={`co-${k}`} value={f[k] || ''} onChange={set(k)} className={invalid(k) ? 'p-invalid' : ''}
                aria-required={req.includes(k) || undefined} aria-invalid={invalid(k) || undefined} />
        </div>
    );

    return (
        <FormDialog visible title={title} canSave={valid} onClose={onClose} onSave={save}
            dirty={dirty} busy={busy} width="46rem">
            <div className="form-section">
                <h4 className="form-section-title">Osnovni podaci</h4>
                <div className="form-grid cols-2">
                    {text('tin', 'PIB')}
                    {text('name', 'Naziv')}
                    {text('address', 'Adresa')}
                    {text('country', 'Država')}
                    <div className="field">
                        <label htmlFor="co-issue">Datum sklapanja ugovora<span className="req" aria-hidden="true">*</span></label>
                        <DateInput inputId="co-issue" value={f.contractIssueDate} onChange={set('contractIssueDate')} dateFormat="dd.mm.yy" showIcon
                            monthNavigator yearNavigator yearRange="2010:2030" className={dateInvalid ? 'p-invalid' : ''} />
                    </div>
                    <div className="field">
                        <label htmlFor="co-expiry">Datum isteka ugovora</label>
                        <DateInput inputId="co-expiry" value={f.contractExpiryDate} onChange={set('contractExpiryDate')} dateFormat="dd.mm.yy" showIcon
                            monthNavigator yearNavigator yearRange="2010:2030" />
                    </div>
                </div>
            </div>
            <div className="form-section">
                <h4 className="form-section-title">Kontakt</h4>
                <div className="form-grid cols-2">
                    {text('email', 'Email')}
                    {text('phone', 'Telefon')}
                    {text('fax', 'Fax')}
                    {text('city', 'Grad')}
                    {text('mainPerson', 'Odgovorno lice')}
                    {text('mainPersonPosition', 'Pozicija odgovornog lica')}
                </div>
            </div>
        </FormDialog>
    );
}
